import React, { useCallback, useEffect, useRef, useState, FC } from 'react';
import { documentService, windowService } from '../../../../../../utils/window.utils';
import breakpointsConstants from '../../../../../../constants/breakpoints.constants';
import Icon from '../../../../../@shared/icon';
import { iconSize } from '../../../../../../constants/icon.constants';
import './styles.scss';
import ctaTypes from '../../../../../../enums/CTA.enums';
import DictionaryTerm from '../../../../../@shared/dictionaryTerm';
import useAnimationEnd from '../../../../../../hooks/useUnMountOnAnimationEnd';
import {
  TDealerFilterComponentProps,
  TDealerSelectedFilterKeyName,
  TSelectedFilterOptions,
} from '../../../../types/dealerLocatorLanding.types';

export type TDroplistProps = Partial<{
  droplistOptions: {
    Code?: string;
    Title?: JSX.Element | string;
  }[];
  droplistTitle: string | JSX.Element;
  extraClassName: string;
  optionColumnCount: number;
}> & {
  filterKeyName: TDealerSelectedFilterKeyName;
  selectedFilters: TDealerFilterComponentProps['selectedFilters'];
  updateFilters: TDealerFilterComponentProps['updateFilters'];
};

const Droplist: FC<TDroplistProps> = ({
  droplistOptions = [],
  droplistTitle,
  extraClassName = '',
  optionColumnCount = 1,
  filterKeyName,
  selectedFilters,
  updateFilters,
}) => {
  const [shouldShowDroplist, setShouldShowDroplist] = useState(false);
  const [shouldAlignRight, setShouldAlignRight] = useState(false);
  const droplistElmRef = useRef<HTMLDivElement>(null);
  const canShowDOM = useAnimationEnd(shouldShowDroplist, 300);
  const [localSelectedFilter, setLocalSelectedFilter] = useState<TSelectedFilterOptions>({});
  const showDisabledResetCTA = Object.keys(localSelectedFilter)?.length === 0;

  useEffect(() => {
    setLocalSelectedFilter({ ...selectedFilters[filterKeyName] });
  }, [shouldShowDroplist]);

  useEffect(() => {
    function closeDroplistOnOutsideClick(event): void {
      if (windowService()?.innerWidth < breakpointsConstants['tbl-l']) return;
      if (!droplistElmRef?.current?.contains(event?.target)) {
        setShouldShowDroplist(false);
      }
    }
    const docElement = documentService();
    if (!docElement?.body) return;
    docElement.addEventListener('click', closeDroplistOnOutsideClick, false);
    return (): void => docElement.removeEventListener('click', closeDroplistOnOutsideClick, false);
  }, []);

  // Auto align if droplist overflows outside browser
  useEffect(() => {
    if (!droplistElmRef?.current) return;
    const { right } = droplistElmRef?.current?.getElementsByClassName('dealer-droplist-filter__panel')[0]?.getBoundingClientRect() || {};
    if (right && right > documentService().documentElement?.clientWidth) {
      setShouldAlignRight(true);
    } else {
      setShouldAlignRight(false);
    }
  }, [canShowDOM]);

  const applyFilter = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.stopPropagation();
      const newFilters = { ...selectedFilters };
      newFilters[filterKeyName] = { ...localSelectedFilter };
      updateFilters({ ...newFilters });
      setShouldShowDroplist(false);
    },
    [localSelectedFilter]
  );

  const resetFilter = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.stopPropagation();
      if (showDisabledResetCTA) return;
      const newFilters = { ...selectedFilters };
      newFilters[filterKeyName] = {};
      updateFilters({ ...newFilters });
      setLocalSelectedFilter({});
    },
    [localSelectedFilter]
  );

  const saveLocalSelectedFilter = (Code: string, filterIndex: number, isChecked) => {
    const localSelectedFilterCopy = localSelectedFilter;
    const selectedFilterIndexArray = localSelectedFilterCopy[Code];
    if (selectedFilterIndexArray) {
      if (isChecked) {
        localSelectedFilterCopy[Code] = localSelectedFilterCopy[Code].filter(
          (eachSelectedFilterInd) => eachSelectedFilterInd !== filterIndex
        );
      } else {
        localSelectedFilterCopy[Code] = [...selectedFilterIndexArray, filterIndex];
      }
    } else {
      localSelectedFilterCopy[Code] = [filterIndex];
    }
    if (localSelectedFilter[Code]?.length === 0) delete localSelectedFilterCopy[Code];
    setLocalSelectedFilter({ ...localSelectedFilterCopy });
  };

  if (!droplistOptions.length) return <></>;

  const panelStyles = {
    '--option-column-count': optionColumnCount,
  } as React.CSSProperties;

  return (
    <div
      className={'dealer-droplist-filter' + (extraClassName ? ` ${extraClassName}` : '')}
      ref={droplistElmRef}
      onClick={(event) => {
        event.stopPropagation();
        setShouldShowDroplist(!shouldShowDroplist);
      }}
    >
      <span className="dealer-droplist-filter__title">{droplistTitle}</span>
      <Icon
        iconName={`arrow-chevron-${shouldShowDroplist ? 'up' : 'down'}`}
        size={iconSize['dsk']}
        extraClassName="dealer-droplist-filter__icon"
        color={'primary'}
      />
      {canShowDOM && (
        <div
          className={`dealer-droplist-filter__panel 
            ${shouldShowDroplist ? 'dealer-droplist-filter__panel--open' : 'dealer-droplist-filter__panel--closed'}
            ${shouldAlignRight ? 'dealer-droplist-filter__panel--right' : ''}
          `}
          style={panelStyles}
        >
          <ul className="dealer-droplist-filter__option-wrapper">
            {droplistOptions?.map(({ Code = '', Title = '' }, index) => {
              const isChecked = localSelectedFilter[Code]?.includes?.(index);

              return (
                <li
                  className="dealer-droplist-filter__option"
                  onClick={(event) => {
                    event.stopPropagation();
                    Code && saveLocalSelectedFilter(Code, index, isChecked);
                  }}
                >
                  <Icon iconName={isChecked ? 'action-checkbox-checked' : 'action-checkbox-default'} size={iconSize['dsk']}/>
                  <span>{Title}</span>
                </li>
              );
            })}
          </ul>
          <div className="dealer-droplist-filter__cta-wrapper">
            <button
              onClick={resetFilter}
              className={`button
              ${ctaTypes['secondaryWhite']} dealer-droplist-filter__cta ${
                showDisabledResetCTA ? ' dealer-droplist-filter__cta--disabled' : ' dealer-droplist-filter__cta--reset-filters'
              }`}
            >
              <DictionaryTerm keyword="cnhi-dealerLocatorResetFilter" />
            </button>
            <button
              onClick={applyFilter}
              className={`button ${ctaTypes['primaryOrange']} dealer-droplist-filter__cta dealer-droplist-filter__cta--apply-filters`}
            >
              <DictionaryTerm keyword="cnhi-dealerLocatorApplyFilter" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Droplist;
